import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  forwardRef
} from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { Popper, styled, useTheme } from "@mui/material";
import { I_GENERIC_ITEMS } from "@/src-refactor/modules/shared/model/interface/items.interface";
import { Csizes } from "@/shared/theme/theme";
import MxLoading from "@rf-l1/loading/MxLoading";
import { MxAutoCompleteFieldProps } from "./model/MxAutoCompleteField";
import { useClickOutside } from "./useClickOutside";
import { useDebouncedCallback } from "./useDebouncedCallback";
import SelectedChips from "./SelectedChips";
import DropdownContent from "./DropdownContent";

const Container = styled("div")<{
  invalid?: boolean;
  hasValue: boolean;
  hasFocus: boolean;
  background?: string;
  disabled?: boolean;
}>(({ theme, invalid, hasValue, hasFocus, background, disabled }) => ({
  position: "relative",
  width: "100%",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  border: `1px solid ${
    invalid
      ? theme.palette.error.main
      : hasFocus
      ? theme.palette.primary.main
      : theme.palette.grey[300]
  }`,
  borderRadius: "100px",
  boxSizing: "border-box",
  height: Csizes.input,
  background: disabled ? "#f1f5f9" : background || "transparent",
  pointerEvents: disabled ? "none" : "auto"
}));

const FilterButton = styled("button")<{ isOpen: boolean; disabled?: boolean }>(
  ({ isOpen, disabled }) => ({
    width: "100%",
    minHeight: "32px",
    minWidth: 0,
    padding: "4px 12px 4px 8px",
    background: "transparent",
    border: `2px solid ${isOpen ? "transparent" : "transparent"}`,
    borderRadius: "100px",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: "4px",
    fontSize: "14px",
    color: "#172b4d",
    transition: "all 0.2s",
    overflow: "visible",
    position: "relative",
    "& .mx-autocomplete__addon": {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0
    },
    "&:hover": {
      background: "transparent"
    }
  })
);

const CountChip = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "24px",
  height: "24px",
  background: theme.palette.grey[300],
  color: theme.palette.text.primary,
  borderRadius: "50%",
  fontSize: "12px",
  fontWeight: 600,
  marginLeft: "4px",
  position: "absolute",
  right: "0px",

  top: "0px",
  transform: "translate(50%, -50%)"
}));

const LabelText = styled("div")<{ hasValue: boolean; hasFocus: boolean }>(
  ({ theme, hasValue, hasFocus }) => ({
    position: "absolute",
    top: 0,
    left: "12px",
    transform: hasValue || hasFocus ? "translateY(-65%)" : "translateY(30%)",
    backgroundColor:
      hasValue || hasFocus ? theme.palette.background.paper : "transparent",
    fontSize:
      hasValue || hasFocus
        ? theme.typography.pxToRem(10)
        : theme.typography.pxToRem(13),
    padding: hasValue || hasFocus ? "0 4px" : "0",
    fontWeight: 600,
    color: theme.palette.primary.main,
    transition: "all 0.2s ",
    pointerEvents: "none",
    zIndex: 1,
    maxWidth: "calc(100% - 24px)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  })
);

export const AutoCompleteItemOption = styled("div")<{ isSelected: boolean }>(
  ({ isSelected, theme }) => ({
    fontSize: theme.typography.caption.fontSize,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: theme.palette.grey[600],
    background: isSelected ? "#DEEBFF" : "transparent",
    "&:hover": {
      background: isSelected ? "#DEEBFF" : "#F4F5F7"
    },
    "& .icon": {
      marginRight: "8px",
      width: "16px",
      height: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  })
);

const OptionContent = styled("div")({
  flex: 1,
  "& .label": {
    fontWeight: 500
  },
  "& .description": {
    fontSize: "12px",
    color: "#6b778c",
    marginTop: "2px"
  }
});

const Placeholder = styled("span")(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: theme.typography.caption.fontSize
}));

export const MxAutoCompleteField = forwardRef<
  HTMLDivElement,
  MxAutoCompleteFieldProps
>(
  (
    {
      items,
      value,
      onChange,
      placeholder = "Seleccionar...",
      label,
      onSearch,
      isMultiple = false,
      name = "",
      onBlur,
      usePortal = false,
      componentItem: CustomComponent,
      useCustomComponent = false,
      loading = false,
      id,
      testId,
      error: invalid = false,
      showChips = true,
      background,
      placeholderSearch = "Buscar...",
      inputRef,
      disabled = false,
      onSelect,
      isSearch,
      hiddenSearch = false,
      hiddenChips = false,
      hiddenSelections = false
    },
    ref
  ) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const innerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const hasValue = value && value.length > 0;
    const [hasFocus, setHasFocus] = useState(false);

    const debouncedSearch = useDebouncedCallback((text: string) => {
      onSearch?.(text);
    }, 300);

    const handleSearch = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value;
        setSearchText(text);
        debouncedSearch(text);
      },
      [debouncedSearch]
    );

    const handleToggleOption = useCallback(
      (option: I_GENERIC_ITEMS) => {
        const isSelected = value.some(item => item.value === option.value);
        let newValue: I_GENERIC_ITEMS[];

        if (isSelected) {
          newValue = value.filter(item => item.value !== option.value);
        } else {
          if (isMultiple) {
            newValue = [...value, option];
          } else {
            newValue = [option];
            setIsOpen(false);
          }
        }

        onChange({ target: { name, value: newValue } });
        onSelect?.(
          { target: { name, value: newValue } },
          newValue,
          isSelected ? undefined : option
        );
        onBlur?.({ target: { name, value } });
      },
      [value, onChange, isMultiple, name, onSelect, onBlur]
    );

    const removeOption = useCallback(
      (optionId: string | number) => {
        const removedOption = value.find(item => item.value === optionId);
        const newValue = value.filter(item => item.value !== optionId);

        onChange({ target: { name, value: newValue } });
        if (removedOption) {
          onSelect?.({ target: { name, value: newValue } }, newValue, undefined);
        }
      },
      [value, onChange, name, onSelect]
    );

    const filteredItems = useMemo(() => {
      if (!isSearch) {
        return items.filter(
          item =>
            item.label.toLowerCase().includes(searchText.toLowerCase()) &&
            !value.some(val => val.value === item.value)
        );
      } else {
        return items.filter(item => !value.some(val => val.value === item.value));
      }
    }, [items, searchText, value]);

    // Helper function to determine if custom component should be used for a given item
    const shouldUseCustomComponent = useCallback(
      (item: I_GENERIC_ITEMS) => {
        if (typeof useCustomComponent === "function") {
          return useCustomComponent(item);
        }
        return !!useCustomComponent && !!CustomComponent;
      },
      [useCustomComponent, CustomComponent]
    );

    // Render function for each option - handles both default and custom components
    const renderOptionItem = useCallback(
      (item: I_GENERIC_ITEMS, isSelected: boolean, index: number) => {
        const handleClick = () => handleToggleOption(item);

        if (CustomComponent && shouldUseCustomComponent(item)) {
          return (
            <CustomComponent
              key={`${item.id || item.value}-${index}`}
              id={`${item.id || item.value}-${index}`}
              data-testid={`${item?.id || item?.value}-${index}-item`}
              item={item}
              isSelected={isSelected}
              onClick={handleClick}
            />
          );
        }

        return (
          <AutoCompleteItemOption
            key={`${item?.id || item?.value}-${index}`}
            id={`${item?.id || item?.value}-${index}`}
            data-testid={`${item?.id || item?.value}-${index}-item`}
            isSelected={isSelected}
            onClick={handleClick}
            role="option"
          >
            <OptionContent>
              <div className="label">{item.label}</div>
              {item.description && (
                <div className="description">{item.description}</div>
              )}
            </OptionContent>
          </AutoCompleteItemOption>
        );
      },
      [handleToggleOption, CustomComponent, shouldUseCustomComponent]
    );

    const dropdownContent = useMemo(
      () => (
        <DropdownContent
          isOpen={isOpen}
          dropdownRef={dropdownRef}
          searchInputRef={searchInputRef}
          placeholderSearch={placeholderSearch}
          searchText={searchText}
          onSearchChange={handleSearch}
          value={value}
          filteredItems={filteredItems}
          renderOptionItem={renderOptionItem}
          id={id}
          hiddenSearch={hiddenSearch}
          hiddenChips={hiddenChips}
          hiddenSelections={hiddenSelections}
          maxHeight={usePortal ? 200 : undefined}
        />
      ),
      [
        isOpen,
        searchText,
        handleSearch,
        value,
        filteredItems,
        renderOptionItem,
        usePortal
      ]
    );

    const renderDropdown = useCallback(() => {
      if (usePortal) {
        if (!isOpen) return null;
        return (
          <Popper
            open={isOpen}
            anchorEl={buttonRef.current}
            placement="bottom-start"
            style={{ zIndex: 10000, width: buttonRef.current?.offsetWidth }}
          >
            {dropdownContent}
          </Popper>
        );
      }

      return dropdownContent;
    }, [isOpen, usePortal, buttonRef, dropdownContent]);

    const handleFocus = useCallback(() => {
      setHasFocus(true);
    }, []);

    const handleInputBlur = useCallback(() => {
      console.log("handleInputBlur");
      setHasFocus(false);

      if (onBlur) {
        onBlur({ target: { name, value } });
      }
    }, [onBlur, name, value]);

    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }, [isOpen]);

    const handleClick = () => {
      if (!disabled) {
        setIsOpen(prev => !prev);
        onBlur?.({ target: { name, value } });
      }
    };

    // click fuera (main + dropdown)
    useClickOutside(
      [innerRef, dropdownRef],
      () => {
        setIsOpen(false);
        onBlur?.({ target: { name, value } });
      },
      isOpen
    );

    return (
      <Container
        ref={ref || innerRef}
        invalid={invalid}
        data-testid={testId}
        hasValue={hasValue}
        hasFocus={hasFocus || isOpen}
        background={background}
        disabled={disabled}
      >
        {label && (
          <LabelText
            hasValue={hasValue}
            hasFocus={hasFocus || isOpen}
            className="MuiFormLabel-root"
            id={`${id}-label`}
            data-testid={`${id}-label`}
          >
            {label}
          </LabelText>
        )}
        <FilterButton
          ref={inputRef || buttonRef}
          isOpen={isOpen}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${id}-options-list`}
          onClick={handleClick}
          onFocus={handleFocus}
          onBlur={handleInputBlur}
          type="button"
          id={id}
          data-testid={`${id}-button-search`}
          disabled={disabled}
        >
          {value.length > 0 ? (
            <>
              {showChips && (
                <SelectedChips items={value} onRemove={removeOption} id={id} />
              )}
              {value.length > 0 && isMultiple && (
                <CountChip id={`${id}-count-chip`} data-testid={`${id}-count-chip`}>
                  {value.length}
                </CountChip>
              )}
            </>
          ) : (
            <>
              {label && <></>}
              {!label && (
                <Placeholder
                  id={`${id}-placeholder`}
                  data-testid={`${id}-placeholder`}
                >
                  {placeholder}
                </Placeholder>
              )}
            </>
          )}

          {loading ? (
            <span className="mx-autocomplete__addon">
              <MxLoading size={16} />
            </span>
          ) : (
            <span className="mx-autocomplete__addon">
              <IconChevronDown
                size={16}
                id={`${id}-icon-chevron-down`}
                data-testid={`${id}-icon-chevron-down`}
              />
            </span>
          )}
        </FilterButton>
        {renderDropdown()}
      </Container>
    );
  }
);

// Add display name for better debugging
MxAutoCompleteField.displayName = "MxAutoCompleteField";

// Memoized components (definidos después de la declaración para evitar errores de hoisting)
export const MemoizedOption = React.memo(AutoCompleteItemOption);
export const MemoizedOptionContent = React.memo(OptionContent);

export default MxAutoCompleteField;
